import React, {useContext, useEffect, useState} from "react";
import { AuthContext, IntlContext } from "context";
import { TextField, Checkbox, Radio, Autocomplete } from "components/Form/Field";
import Address from "components/Form/Address";
import {useForm, useFormState} from "react-final-form";
import { Gender } from "types/user";
import { fetchAutocompleteCompany } from "hooks/autocomplete";
import { checkCesuNumPe, checkCesuNumSp, generateCesuAttestation } from "hooks/cesu";
import { CodeCesu } from "types/cesu";
import { getErrorResponseData, hasKey, isRecord } from "helpers/typing";
import { FontAwesomeIcon as Icon } from "@fortawesome/react-fontawesome";

const OurServicesResumeInformation: React.FC = () => {
    const intl = useContext(IntlContext);
    const { values } = useFormState();
    const form = useForm();
    const { isLoggedIn, account } = useContext(AuthContext);
    const [hasCesuSPValid, setHasCesuSPValid] = useState<boolean>(false);
    const [hasCesuPEValid, setHasCesuPEValid] = useState<boolean>(false);
    const [hasCesuValid, setHasCesuValid] = useState<boolean>(false);
    const [hasErrCesu, setHasErrCesu] = useState<boolean>(false);
    const [hasErrCesuSalarie, setHasErrCesuSalarie] = useState<boolean>(false);
    const [hasCesuPlusActivated, setHasCesuPlusActivated] = useState<boolean>(false);
    const [hasAiciActivated, setHasAiciActivated] = useState<boolean>(false);
    const [attestIsLoading, setAttestIsLoading] = useState<boolean>(false);
    const [attestIsGenerated, setAttestIsGenerated] = useState<boolean>(false);
    const [attestHasError, setAttestHasError] = useState<boolean>(false);

    const formatCompanyLabel = (company: Record<string, unknown>) => {
        return typeof company.name === "string" ? company.name : "";
    };

    const generateAttestation = async () => {
        try {
            if (values?.booking_id) {
                setAttestIsLoading(true);
                await generateCesuAttestation(values.booking_id).then(() => {
                    setAttestIsLoading(false);
                    setAttestIsGenerated(true);
                    form.change('attestation_generated', true);
                });
            }
        } catch {
            setAttestIsLoading(false);
            setAttestHasError(true);
        }
    };

    useEffect(() => {
        if (account?.resources?.client[0]?.code_cesu && account?.resources?.client[0]?.code_cesu !== CodeCesu.WITHOUT_CESU && account?.resources?.client[0]?.num_cesu && values.provider_cesu) {
            checkCesuNumPe(
                account?.resources?.client[0]?.num_cesu,
                account?.resources?.client[0]?.birthday,
                values.provider_cesu
            ).then((response) => {
                if (response && response.inscrit && response.mandatPresent) {
                    setHasCesuPEValid(response.inscrit);
                }
                if (response.etatCesuPlus === "activé") {
                    setHasCesuPlusActivated(true);
                }
                if (response.etatAICI) {
                    setHasAiciActivated(true);
                }
            }).catch((err: unknown) => {
                let error = "";
                const errData = getErrorResponseData(err);
            
                if (
                    errData &&
                    isRecord(errData.data) &&
                    hasKey(errData.data, "error") &&
                    isRecord(errData.data.error) &&
                    typeof errData.data.error.description === "string"
                ) {
                    const errDesc = JSON.parse(errData.data.error.description);
                    error = errDesc.description;
                }

                if(error.startsWith("Il n'existe aucun lien")) {
                    setHasErrCesu(true);
                }
            });
        }

        if (values.provider_id) {
            checkCesuNumSp(values.provider_id).then((response) => {
                if (response && response.inscrit) {
                    setHasCesuSPValid(response.inscrit);
                }
                else {
                    setHasErrCesuSalarie(true);
                }
            });
        }
    }, [account?.resources?.client, values.provider_id, values.provider_cesu]);

    useEffect(() => {
        if (hasCesuSPValid && hasCesuPEValid) {
            setHasCesuValid(true);

            if(!hasCesuPlusActivated) {
                form.change('activate_cesu_plus', '0');
            }
            else {
                form.change('cesu_plus_status', true);
            }

            if(!hasAiciActivated) {
                form.change('activate_cesu_plus', '0');
            } else {
                form.change('aici_status', true);
            }
        }
    }, [hasCesuSPValid, hasCesuPEValid, hasCesuPlusActivated, hasAiciActivated, form]);

    return (
        <React.Fragment>
            <h3 className="margin-bottom--3">{intl.formatMessage("our_services.form.label.your_informations")}</h3>
            <div className="grid--12 grid--medium-1 grid--small-1 grid--has-gutter-3x">
                {(!isLoggedIn || account?.resources?.client === undefined) && (
                    <div className="grid__col--12">
                        <Radio
                            fieldControlProps={{
                                className: "field-control-radio field__row--inline",
                            }}
                            labelProps={{
                                label: intl.formatMessage("our_services.form.label.gender"),
                            }}
                            inputProps={{
                                name: "gender",
                                required: true,
                                options: [
                                    {
                                        value: Gender.FEMALE,
                                        label: intl.formatMessage("generic.label.gender.FEMALE"),
                                    },
                                    {
                                        value: Gender.MALE,
                                        label: intl.formatMessage("generic.label.gender.MALE"),
                                    },
                                ],
                            }}
                        />
                    </div>
                )}
                <div className="grid__col--6 grid__col--small-12">
                    <TextField
                        inputProps={{
                            name: "client_first_name",
                            autoComplete: "first_name",
                            required: true,
                            placeholder: intl.formatMessage("our_services.form.label.first_name"),
                        }}
                        labelProps={{
                            label: intl.formatMessage("our_services.form.label.first_name"),
                        }}
                    />
                </div>
                <div className="grid__col--6 grid__col--small-12">
                    <TextField
                        inputProps={{
                            name: "client_last_name",
                            autoComplete: "last_name",
                            required: true,
                            placeholder: intl.formatMessage("our_services.form.label.last_name"),
                        }}
                        labelProps={{
                            label: intl.formatMessage("our_services.form.label.last_name"),
                        }}
                    />
                </div>
                {!isLoggedIn || !account?.resources?.client ? (
                    <div className="grid__col--12">
                        <Address
                            inputProps={{
                                name: "client_address",
                                latitudeName: "client_coordinates_lat",
                                longitudeName: "client_coordinates_lon",
                                houseNumber: "client_house_number",
                                street: "client_street",
                                postcode: "client_postcode",
                                city: "client_city",
                                required: true,
                            }}
                        />
                    </div>
                ) : (
                    <React.Fragment>
                        <div className="grid__col--12">
                            <Address
                                inputProps={{
                                    name: "client_address",
                                    latitudeName: "client_coordinates_lat",
                                    longitudeName: "client_coordinates_lon",
                                    houseNumber: "client_house_number",
                                    street: "client_street",
                                    postcode: "client_postcode",
                                    city: "client_city",
                                    required: true,
                                }}
                                labelProps={{
                                    label: intl.formatMessage("our_services.form.label.address"),
                                }}
                            />
                        </div>
                    </React.Fragment>
                )}
                <div className="grid__col--12">
                    <Checkbox
                        inputProps={{
                            name: "same_address_invoice",
                            options: [
                                {
                                    value: "1",
                                    label: intl.formatMessage("our_services.form.label.use_same_address_for_invoice"),
                                },
                            ],
                        }}
                    />
                </div>
                {(!values.same_address_invoice || !values.same_address_invoice.length) && (
                    <div className="grid__col--12">
                        <Address
                            labelProps={{
                                label: intl.formatMessage("our_services.form.label.invoice_address"),
                            }}
                            inputProps={{
                                name: "invoice_address",
                                latitudeName: "invoice_coordinates_lat",
                                longitudeName: "invoice_coordinates_lon",
                                houseNumber: "invoice_house_number",
                                street: "invoice_street",
                                postcode: "invoice_postcode",
                                city: "invoice_city",
                                required: true,
                            }}
                        />
                    </div>
                )}
                <div className="grid__col--12">
                    <TextField
                        inputProps={{
                            name: "client_phone",
                            autoComplete: "phone",
                            required: true,
                            maxLength: 10,
                            placeholder: intl.formatMessage("our_services.form.label.phone"),
                        }}
                        labelProps={{
                            label: intl.formatMessage("our_services.form.label.phone"),
                            requiredSymbole: "",
                        }}
                    />
                </div>
                {!isLoggedIn || (!account?.resources?.client && (
                    <React.Fragment>
                        <p className="h3 margin-top--2 grid__col--12">
                            {intl.formatMessage("our_services.form.label.affiliate_has_company")}
                        </p>
                        <div className="grid__col--12">
                            <Autocomplete
                                inputProps={{
                                    name: "company_id",
                                    keySearch: "filter_name_with_valid_signed_date",
                                    dataValue: "id",
                                    dataLabel: formatCompanyLabel,
                                    dataFetcher: fetchAutocompleteCompany,
                                }}
                                labelProps={{
                                    label: intl.formatMessage("our_services.form.label.name_company"),
                                }}
                            />
                        </div>

                        <div className="grid__col--12">
                            <TextField
                                inputProps={{
                                    name: "code_company",
                                    placeholder: intl.formatMessage("our_services.form.label.code_company"),
                                }}
                                labelProps={{
                                    label: intl.formatMessage("our_services.form.label.code_company"),
                                }}
                            />
                        </div>
                    </React.Fragment>
                ))}
            </div>

            <h3 className="margin-top--5 margin-bottom--3">Gestion du <a href="https://www.cesu.urssaf.fr/info/accueil/utiliser-le-cesu/le-service-cesu/le-cesu--quest-ce-que-cest.html" target="_blank" className="text-color--primary bold">CESU+</a> et de <a href="https://www.cesu.urssaf.fr/info/accueil/beneficier-d-avantages/lavantage-fiscal/cesu-avance-immediate-quest-ce-q.html" target="_blank" className="text-color--primary bold">l'AICI</a></h3>
            {hasCesuValid && (
                <div className="grid--12 grid--medium-1 grid--small-1 grid--has-gutter-3x">
                    {!hasCesuPlusActivated || !hasAiciActivated ? (
                        <div className="grid__col--12">
                            <div className="flex flex--align-items-start">
                                <Radio
                                    fieldControlProps={{
                                        className: "field-control-radio field__row--inline",
                                    }}
                                    inputProps={{
                                        name: "activate_cesu_plus",
                                        required: true,
                                        onChange: (e) => {
                                            form.change('activate_cesu_plus', e.target.value);
                                        },
                                        options: [
                                            {
                                                value: "1",
                                                label: intl.formatMessage("generic.label.yes"),
                                            },
                                            {
                                                value: "0",
                                                label: intl.formatMessage("generic.label.no"),
                                            },
                                        ],
                                    }}
                                    labelProps={{
                                        label: hasAiciActivated ? intl.formatMessage("our_services.form.label.activate_cesu_plus") : intl.formatMessage("our_services.form.label.activate_cesu_plus_aici"),
                                    }}
                                />
                                <div>
                                    <span className="btn-tooltip" style={{ marginLeft: "8px" }}>
                                        <span className="icon icon-info"></span>

                                        <span className="btn-tooltip__content">
                                            {intl.formatMessage("our_services.form.label.activate_cesu_plus.tooltip")}
                                        </span>
                                    </span>
                                </div>
                            </div>
                            {!hasCesuPlusActivated && values?.activate_cesu_plus === '1' && (
                                <div>
                                    {!attestIsLoading && !attestIsGenerated && !attestHasError && (
                                        <div>
                                            <span className="comment text-color--danger">Attention, vous devez d'abord demander l'accord du salarié prestataire au préalable&nbsp;!</span><br />
                                            <a href="javascript:void(0)" className="comment text-color--primary" onClick={generateAttestation}>Générer l'attestation d'adhésion CESU+ à signer <Icon icon={{ prefix: "fas", iconName: "download" }} /></a>
                                        </div>
                                    )}
                                    {attestIsLoading && (
                                    <div className="flex flex--align-items-center">
                                        <span className="comment">Génération en cours...</span>
                                        <div className="loader" style={{ width:"12px", height:"12px", border:"1px solid #000", borderBottomColor:"transparent", marginLeft:"4px" }} />
                                    </div>
                                    )}
                                    {attestIsGenerated && (
                                        <p className="comment text-color--success-dark">
                                            <Icon icon={{ prefix: "fas", iconName: "check" }} /> Attestation générée, un e-mail vous a été envoyé ainsi qu'au salarié pour procéder à la signature électronique du document.
                                        </p>
                                    )}
                                    {attestHasError && (
                                        <p className="comment text-color--danger">{intl.formatMessage("generic.error")}</p>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : <div className="grid__col--12"><p className="comment text-color--success-dark"><Icon icon={{ prefix: "fas", iconName: "check" }} /> Service <strong>CESU+</strong> déjà activé avec ce prestataire.</p></div>}

                    {hasAiciActivated && (
                        <div className="grid__col--12"><p className="comment text-color--success-dark"><Icon icon={{ prefix: "fas", iconName: "check" }} /> Service <strong>Avance Immédiate de Crédit d'Impôt</strong> déjà activé sur votre compte Cesu.</p></div>
                    )}
                </div>
            )}
            {hasErrCesu && (
                <div className="grid__col--12">
                    <i>Pour pouvoir activer le service <a href="https://www.cesu.urssaf.fr/info/accueil/utiliser-le-cesu/le-service-cesu/le-cesu--quest-ce-que-cest.html" target='_blank' className="bold">CESU+</a> avec ce salarié, vous devez l'avoir déjà employé au préalable, ou l'ajouter sur votre <a href='https://www.cesu.urssaf.fr/decla/index.html?page=page_empl_mes_salaries' target='_blank' className="bold">compte CESU</a> (rubrique "Gérer mon compte" -&gt; "Mes salariés").</i>
                </div>
            )}
            {hasErrCesuSalarie && (
                <div className="grid__col--12">
                    <i>Ce salarié n'a pas encore de n° CESU, il le recevra par courrier après sa 1ère prestation.</i>
                </div>
            )}
        </React.Fragment>
    );
};

export default OurServicesResumeInformation;
